import { postRequest, getRequest } from "../api/apiService";

// Fixed ID for the domain, used for API requests
const id = 0;

/**
 * Updates the domain name by sending a POST request to the server.
 * 
 * This function constructs a payload with the domain ID and the new domain name, then sends
 * the data to the server using the `postRequest` function. If the request is successful, it logs
 * the response and returns a success object. If an error occurs, it logs the error and returns a failure object.
 * 
 * @param {string} domainName - The new domain name to update.
 * 
 * @returns {Promise<Object>} An object indicating the success or failure of the operation, along with the response data or error.
 */
export const updateDomainName = async (domainName) => {
  const payload = {
    id: id,            // Domain ID is fixed and passed along with the new domain name
    domainName: domainName, // New domain name provided by the user
  };

  try {
    // Call the POST request to update the domain
    const response = await postRequest("/api/domain", payload);
    // Return success response with data
    return { success: true, data: response };
  } catch (error) {
    return { success: false, error };
  }
};

/**
 * Fetches the domain name using a GET request to the server.
 * 
 * This function sends a GET request to the server to retrieve the domain name using the fixed ID.
 * If the request is successful, it logs the response and returns the domain data.
 * If an error occurs, it logs the error and throws it for further handling.
 * 
 * @returns {Promise<Object>} The response data from the GET request, assumed to be an array of assets.
 * 
 * @throws {Error} Throws an error if the GET request fails.
 */
export const fetchDomainName = async () => {
  try {
    // Fetch the domain data by sending a GET request using the domain ID
    const response = await getRequest(`/api/domain/${id}`);
    return response.data; // Assuming the response contains data in the 'data' field
  } catch (error) {
    throw error; // Rethrow the error for further handling
  }
};
